/**
 * fleet.ts — the chassis catalog + component math of the space RPG (pure).
 *
 * Chassis flight stats are CLIENT data (the sector-layout law); the server
 * mirrors only costs + rank gates (server/src/lib/constants.ts FLEET_CHASSIS
 * — keep the two tables' costSov/rank columns identical). Component items
 * (`comp_<slot>` playerItems, quality 0–1000) multiply the base stats;
 * `shipStats` derives the flown numbers from chassis + equipped components.
 */

export interface ChassisSpec {
  key: string;
  name: string;
  role: string;
  prefab: string;        // prop:<key> — falls back to the stock fighter hull until cooked
  costSov: number;       // MIRROR of server FLEET_CHASSIS
  rank: number;          // MIRROR of server FLEET_CHASSIS
  hullSize: number;      // rendered length (m)
  maxSpeed: number;      // m/s at 100% throttle
  hp: number;            // hull integrity
  shield: number;        // shield pool
  capacitor: number;     // weapon energy pool
  boost: number;         // booster reservoir multiplier
  cargo: number;         // cargo units (mining/hauling hooks)
  turnRate: number;      // steering multiplier (interceptors dance, gunships lumber)
  /** Default chase distance (m behind the hull's centre) for a hull too long for the shared 15 m (cockpit_zoom.chaseFor); absent = the default. */
  chaseM?: number;
  /** The Sovereign carries a walkable deck + a dorsal turret station. */
  walkable?: boolean;
}

export const CHASSIS: Record<string, ChassisSpec> = {
  ship_lancer: {
    key: 'ship_lancer', name: 'Lancer', role: 'strike fighter', prefab: 'prop:ship_lancer',
    costSov: 0, rank: 0, hullSize: 9, maxSpeed: 105, hp: 100, shield: 60, capacitor: 100,
    boost: 1, cargo: 4, turnRate: 1,
  },
  ship_talon: {
    key: 'ship_talon', name: 'Talon', role: 'interceptor', prefab: 'prop:ship_talon',
    costSov: 1200, rank: 1, hullSize: 8, maxSpeed: 132, hp: 70, shield: 45, capacitor: 85,
    boost: 1.25, cargo: 2, turnRate: 1.3,
  },
  ship_courier: {
    key: 'ship_courier', name: 'Courier', role: 'cargo runner', prefab: 'prop:ship_courier',
    costSov: 1800, rank: 1, hullSize: 12, maxSpeed: 96, hp: 120, shield: 70, capacitor: 80,
    boost: 0.9, cargo: 24, turnRate: 0.8,
  },
  ship_bulwark: {
    key: 'ship_bulwark', name: 'Bulwark', role: 'heavy gunship', prefab: 'prop:ship_bulwark',
    costSov: 3500, rank: 2, hullSize: 13, maxSpeed: 84, hp: 220, shield: 130, capacitor: 150,
    boost: 0.8, cargo: 8, turnRate: 0.62,
  },
  ship_sovereign: {
    key: 'ship_sovereign', name: 'Sovereign', role: 'personal cruiser', prefab: 'prop:ship_sovereign',
    costSov: 9000, rank: 3, hullSize: 26, maxSpeed: 78, hp: 380, shield: 220, capacitor: 200,
    boost: 0.7, cargo: 40, turnRate: 0.45, walkable: true,
  },
  // ── The 2026-10-08 wave: twenty more hulls (docs/research/2026-10-08-ships-vehicles/PLAN.md "Phase 2"). STARTING
  //    values — proposals for the owner to tune. cost / rank / cargo MIRROR server FLEET_CHASSIS; maxSpeed / boost /
  //    capacitor MIRROR CHASSIS_COMBAT; hp CHASSIS_HP (the harnesses compare each). ──
  ship_wren: {
    key: 'ship_wren', name: 'Wren', role: 'light scout', prefab: 'prop:ship_wren',
    costSov: 700, rank: 0, hullSize: 7, maxSpeed: 118, hp: 60, shield: 40, capacitor: 70,
    boost: 1.1, cargo: 3, turnRate: 1.2,
  },
  ship_packet: {
    key: 'ship_packet', name: 'Packet', role: 'fast light courier', prefab: 'prop:ship_packet',
    costSov: 900, rank: 0, hullSize: 10, maxSpeed: 110, hp: 80, shield: 50, capacitor: 70,
    boost: 1.15, cargo: 10, turnRate: 0.95,
  },
  ship_pathfinder: {
    key: 'ship_pathfinder', name: 'Pathfinder', role: 'survey hull', prefab: 'prop:ship_pathfinder',
    costSov: 2000, rank: 1, hullSize: 14, maxSpeed: 100, hp: 110, shield: 90, capacitor: 130,
    boost: 1, cargo: 12, turnRate: 0.85, chaseM: 17.5,
  },
  ship_kestrel: {
    key: 'ship_kestrel', name: 'Kestrel', role: 'racer', prefab: 'prop:ship_kestrel',
    costSov: 2400, rank: 1, hullSize: 7.5, maxSpeed: 150, hp: 45, shield: 30, capacitor: 70,
    boost: 1.5, cargo: 1, turnRate: 1.45,
  },
  ship_warden: {
    key: 'ship_warden', name: 'Warden', role: 'escort gunship', prefab: 'prop:ship_warden',
    costSov: 2200, rank: 1, hullSize: 12, maxSpeed: 98, hp: 160, shield: 100, capacitor: 120,
    boost: 0.95, cargo: 6, turnRate: 0.85,
  },
  ship_prospector: {
    key: 'ship_prospector', name: 'Prospector', role: 'light mining hull', prefab: 'prop:ship_prospector',
    costSov: 1600, rank: 1, hullSize: 11, maxSpeed: 90, hp: 130, shield: 70, capacitor: 160,
    boost: 0.85, cargo: 16, turnRate: 0.8,
  },
  ship_gleaner: {
    key: 'ship_gleaner', name: 'Gleaner', role: 'salvage tug', prefab: 'prop:ship_gleaner',
    costSov: 1900, rank: 1, hullSize: 13, maxSpeed: 94, hp: 180, shield: 80, capacitor: 110,
    boost: 0.9, cargo: 18, turnRate: 0.78,
  },
  ship_harrier: {
    key: 'ship_harrier', name: 'Harrier', role: 'heavy interceptor', prefab: 'prop:ship_harrier',
    costSov: 3000, rank: 2, hullSize: 10, maxSpeed: 124, hp: 110, shield: 80, capacitor: 120,
    boost: 1.15, cargo: 3, turnRate: 1.15,
  },
  ship_vesper: {
    key: 'ship_vesper', name: 'Vesper', role: 'duelist', prefab: 'prop:ship_vesper',
    costSov: 3200, rank: 2, hullSize: 9.5, maxSpeed: 116, hp: 90, shield: 100, capacitor: 150,
    boost: 1.1, cargo: 2, turnRate: 1.25,
  },
  ship_farstrider: {
    key: 'ship_farstrider', name: 'Farstrider', role: 'deep-range explorer', prefab: 'prop:ship_farstrider',
    costSov: 4200, rank: 2, hullSize: 18, maxSpeed: 108, hp: 160, shield: 120, capacitor: 140,
    boost: 1.3, cargo: 16, turnRate: 0.8, chaseM: 22.5,
  },
  ship_arbalest: {
    key: 'ship_arbalest', name: 'Arbalest', role: 'missile boat', prefab: 'prop:ship_arbalest',
    costSov: 4000, rank: 2, hullSize: 14, maxSpeed: 88, hp: 150, shield: 110, capacitor: 230,
    boost: 0.85, cargo: 6, turnRate: 0.7, chaseM: 17.5,
  },
  ship_drover: {
    key: 'ship_drover', name: 'Drover', role: 'medium hauler', prefab: 'prop:ship_drover',
    costSov: 3800, rank: 2, hullSize: 20, maxSpeed: 82, hp: 180, shield: 100, capacitor: 90,
    boost: 0.8, cargo: 48, turnRate: 0.6, chaseM: 25,
  },
  ship_tender: {
    key: 'ship_tender', name: 'Tender', role: 'armoured blockade runner', prefab: 'prop:ship_tender',
    costSov: 4400, rank: 2, hullSize: 16, maxSpeed: 92, hp: 240, shield: 150, capacitor: 110,
    boost: 1, cargo: 30, turnRate: 0.68, chaseM: 20,
  },
  ship_delver: {
    key: 'ship_delver', name: 'Delver', role: 'mining barge', prefab: 'prop:ship_delver',
    costSov: 4600, rank: 2, hullSize: 22, maxSpeed: 66, hp: 260, shield: 120, capacitor: 220,
    boost: 0.6, cargo: 60, turnRate: 0.45, chaseM: 27.5,
  },
  ship_laurel: {
    key: 'ship_laurel', name: 'Laurel', role: 'patrol cutter', prefab: 'prop:ship_laurel',
    costSov: 3600, rank: 2, hullSize: 14, maxSpeed: 96, hp: 190, shield: 140, capacitor: 140,
    boost: 0.9, cargo: 10, turnRate: 0.75, chaseM: 17.5,
  },
  ship_rime: {
    key: 'ship_rime', name: 'Rime', role: 'lance cutter', prefab: 'prop:ship_rime',
    costSov: 3600, rank: 2, hullSize: 15, maxSpeed: 104, hp: 150, shield: 120, capacitor: 170,
    boost: 1, cargo: 8, turnRate: 0.85, chaseM: 19,
  },
  ship_ironclad: {
    key: 'ship_ironclad', name: 'Ironclad', role: 'assault gunship', prefab: 'prop:ship_ironclad',
    costSov: 6500, rank: 3, hullSize: 15, maxSpeed: 76, hp: 320, shield: 150, capacitor: 170,
    boost: 0.75, cargo: 6, turnRate: 0.52, chaseM: 19,
  },
  ship_bastion: {
    key: 'ship_bastion', name: 'Bastion', role: 'shield picket', prefab: 'prop:ship_bastion',
    costSov: 6000, rank: 3, hullSize: 16, maxSpeed: 80, hp: 200, shield: 280, capacitor: 180,
    boost: 0.75, cargo: 8, turnRate: 0.55, chaseM: 20,
  },
  ship_argosy: {
    key: 'ship_argosy', name: 'Argosy', role: 'heavy freighter', prefab: 'prop:ship_argosy',
    costSov: 7500, rank: 3, hullSize: 30, maxSpeed: 62, hp: 300, shield: 160, capacitor: 110,
    boost: 0.6, cargo: 80, turnRate: 0.38, chaseM: 37.5,
  },
  ship_reclaimer: {
    key: 'ship_reclaimer', name: 'Reclaimer', role: 'heavy salvage hull', prefab: 'prop:ship_reclaimer',
    costSov: 6800, rank: 3, hullSize: 24, maxSpeed: 70, hp: 320, shield: 140, capacitor: 150,
    boost: 0.65, cargo: 56, turnRate: 0.48, chaseM: 30,
  },
};
export const DEFAULT_CHASSIS = 'ship_lancer';

export const SLOTS = ['reactor', 'engine', 'capacitor', 'shield', 'armor', 'weapon', 'missile', 'counter', 'droidport', 'cargo'] as const;
export type FleetSlot = (typeof SLOTS)[number];

export const SLOT_LABEL: Record<FleetSlot, string> = {
  reactor: 'Reactor', engine: 'Engine', capacitor: 'Capacitor', shield: 'Shield Emitter',
  armor: 'Hull Plating', weapon: 'Cannons', missile: 'Missile Rack', counter: 'Countermeasures',
  droidport: 'Droid Port', cargo: 'Cargo Hold',
};

export interface EquippedComp { slot: string; itemKey: string; quality: number }

export interface ShipStats {
  maxSpeed: number; hp: number; shield: number; capacitor: number;
  boost: number; cargo: number; turnRate: number;
  /** Weapon damage/cooldown multipliers (cannons comp). */
  weaponMult: number; fireCdMult: number;
  /** Missile salvo count per rearm (0 = no rack equipped). */
  missiles: number;
  /** Countermeasure charges per rearm (0 = none). */
  counters: number;
  /** Droid program strength 0..1 (0 = no droid docked). */
  droid: number;
  /** Shield/capacitor regen multipliers (reactor). */
  regenMult: number;
}

/** q 0–1000 → a multiplier 1..(1+span). */
const qMult = (q: number, span: number): number => 1 + Math.max(0, Math.min(1000, q)) / 1000 * span;

/** The three flight-stat skills (Space Wave 1 — the catalogue's promises,
 *  kept): Navigation +3 % speed, Shield Systems +5 % pool, Gunnery +4 %
 *  damage, per level 0–5. */
export interface SkillLevels { gunnery: number; shields: number; navigation: number }
export const NO_SKILLS: SkillLevels = { gunnery: 0, shields: 0, navigation: 0 };
/** A live wormhole's effect on its endpoint systems (shared/space_industry
 *  systemEffectOf): pulsar shields ×1.25, magnetar damage ×1.2, cataclysm
 *  capacitor ×0.75. */
export type SystemEffect = '' | 'pulsar' | 'magnetar' | 'cataclysm';

/** Derived flight/combat stats for a chassis + its equipped components,
 *  the pilot's skills and the system's environment. */
export function shipStats(chassisKey: string, comps: EquippedComp[], skills: SkillLevels = NO_SKILLS, env: { effect: SystemEffect } = { effect: '' }): ShipStats {
  const c = CHASSIS[chassisKey] ?? CHASSIS[DEFAULT_CHASSIS];
  const q = (slot: string): number => comps.find((x) => x.slot === slot)?.quality ?? -1;
  const has = (slot: string): boolean => q(slot) >= 0;
  const stats: ShipStats = {
    maxSpeed: c.maxSpeed * (has('engine') ? qMult(q('engine'), 0.35) : 1),
    hp: Math.round(c.hp * (has('armor') ? qMult(q('armor'), 0.6) : 1)),
    shield: Math.round(c.shield * (has('shield') ? qMult(q('shield'), 0.8) : 1)),
    capacitor: Math.round(c.capacitor * (has('capacitor') ? qMult(q('capacitor'), 0.7) : 1)),
    boost: c.boost,
    cargo: c.cargo + (has('cargo') ? Math.round(q(('cargo')) / 1000 * 20) : 0),
    turnRate: c.turnRate,
    weaponMult: has('weapon') ? qMult(q('weapon'), 0.9) : 1,
    fireCdMult: has('weapon') ? 1 / qMult(q('weapon'), 0.35) : 1,
    missiles: has('missile') ? 2 + Math.floor(q('missile') / 250) : 0,
    counters: has('counter') ? 2 + Math.floor(q('counter') / 300) : 0,
    // A docked SHIP DROID (the starship technician class) nurses the hull 60 % harder than any other droid.
    droid: has('droidport') ? Math.max(0.25, q('droidport') / 1000) * (comps.find((x) => x.slot === 'droidport')?.itemKey === 'droid_ship' ? 1.6 : 1) : 0,
    regenMult: has('reactor') ? qMult(q('reactor'), 0.6) : 1,
  };
  // Pilot skills (Space Wave 1).
  stats.maxSpeed *= 1 + 0.03 * skills.navigation;
  stats.shield = Math.round(stats.shield * (1 + 0.05 * skills.shields));
  stats.weaponMult *= 1 + 0.04 * skills.gunnery;
  // The system's wormhole effect while a live hole touches it.
  if (env.effect === 'pulsar') stats.shield = Math.round(stats.shield * 1.25);
  else if (env.effect === 'magnetar') stats.weaponMult *= 1.2;
  else if (env.effect === 'cataclysm') stats.capacitor = Math.round(stats.capacitor * 0.75);
  return stats;
}

/** Component item keys → the slot they occupy (droids dock at the droid port). */
export function slotOfItem(itemKey: string): FleetSlot | null {
  // THE DROID CLASSES (2026-09-24): any droid docks at the port — except the tank-sized mega.
  if (itemKey.startsWith('droid_')) return itemKey === 'droid_mega' ? null : 'droidport';
  if (!itemKey.startsWith('comp_')) return null;
  const s = itemKey.slice(5) as FleetSlot;
  return (SLOTS as readonly string[]).includes(s) ? s : null;
}
